/**
 * Scroll an element into view without letting the scroll escape this document.
 *
 * ``Element.scrollIntoView`` does not stop at the document it is called in: the
 * CSSOM "scroll an element into view" algorithm walks up the ancestor scrolling
 * boxes and then continues into the browsing context container — the ``iframe``
 * element in the host page. In the partner embed that drags the host page along
 * with us: following a new chat message scrolled the partner's own page down to
 * the bottom on every send.
 *
 * Scrolling the nearest scrollable ancestor directly produces the same movement
 * inside the widget and leaves the host page where the reader put it.
 */

type ScrollBlock = 'start' | 'center' | 'end' | 'nearest';

interface ScrollWithinOptions {
  /** Where to land the element in its scrollport. Mirrors ``scrollIntoView``. */
  block?: ScrollBlock;
  behavior?: ScrollBehavior;
}

const SCROLLABLE_OVERFLOW = /auto|scroll|overlay/;

/**
 * Honor the OS "reduce motion" setting over the caller's request, so every
 * scroll routed through here jumps instead of gliding for users who asked for
 * that. ``matchMedia`` is absent in some test environments, where we animate.
 */
function resolveBehavior(behavior: ScrollBehavior): ScrollBehavior {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return behavior;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : behavior;
}

/**
 * Nearest ancestor that actually scrolls, falling back to this document's own
 * viewport. Scrolling either one stays inside the frame.
 */
function getScrollParent(el: Element): Element {
  let node = el.parentElement;
  while (node && node !== document.body && node !== document.documentElement) {
    const { overflowY } = window.getComputedStyle(node);
    if (SCROLLABLE_OVERFLOW.test(overflowY) && node.scrollHeight > node.clientHeight) return node;
    node = node.parentElement;
  }
  return document.scrollingElement ?? document.documentElement;
}

export function scrollIntoViewWithin(
  el: Element | null | undefined,
  { block = 'start', behavior = 'smooth' }: ScrollWithinOptions = {}
): void {
  if (!el) return;
  const container = getScrollParent(el);
  // The document's scrolling element reports the whole page as its box, so its
  // scrollport is the viewport rather than its own rect.
  const isViewport = container === document.scrollingElement;
  const portTop = isViewport ? 0 : container.getBoundingClientRect().top;
  const portHeight = isViewport ? window.innerHeight : container.clientHeight;

  const rect = el.getBoundingClientRect();
  const offset = rect.top - portTop;
  const slack = portHeight - rect.height;

  let delta: number;
  switch (block) {
    case 'center':
      delta = offset - slack / 2;
      break;
    case 'end':
      delta = offset - slack;
      break;
    case 'nearest':
      if (offset >= 0 && offset + rect.height <= portHeight) return;
      delta = offset < 0 ? offset : offset - slack;
      break;
    default:
      delta = offset;
  }

  const max = container.scrollHeight - container.clientHeight;
  const top = Math.max(0, Math.min(max, container.scrollTop + delta));
  container.scrollTo({ top, behavior: resolveBehavior(behavior) });
}

/**
 * Pin a scrollport to its last row — the chat's "follow the conversation" move.
 * Kept here so callers reach for a container-scoped scroll by default.
 */
export function scrollToEndWithin(
  container: Element | null | undefined,
  behavior: ScrollBehavior = 'smooth'
): void {
  if (!container) return;
  container.scrollTo({ top: container.scrollHeight, behavior: resolveBehavior(behavior) });
}
